// Click-to-edit department label, used in the Grid lane header when
// grouping by Department. Mirrors the EditableTitle / EditableDescription
// pattern in ProjectDetail: a plain span at rest, swaps to a borderless
// input on click, commits on Enter or blur, reverts on Escape. Keeps the
// lane header's layout stable so the dot + count don't shift.

import { useEffect, useRef, useState } from 'react';

import { updateProjectDepartment } from '../../pm/firebase';

interface Props {
  departmentId: string;
  /** The canonical label from RTDB. We re-sync the local draft whenever
   *  this changes from upstream so a realtime update by another user
   *  doesn't get clobbered by stale local input. */
  label: string;
}

const EditableDepartmentLabel: React.FC<Props> = ({ departmentId, label }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync draft when the upstream label changes and we aren't actively
  // editing — keeps a passive lane in sync with realtime writes.
  useEffect(() => {
    if (!editing) setDraft(label);
  }, [label, editing]);

  // Select-all on entering edit mode so typing replaces the existing
  // name immediately.
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  async function commit() {
    setEditing(false);
    const next = draft.trim();
    if (!next || next === label) {
      // Empty or unchanged → just revert visually and skip the write.
      setDraft(label);
      return;
    }
    try {
      await updateProjectDepartment(departmentId, { label: next });
    } catch (err) {
      // Roll back the local draft so the user sees the canonical value
      // again; the failure surfaces in DevTools for debugging.
      // eslint-disable-next-line no-console
      console.error('updateProjectDepartment (label) failed', err);
      setDraft(label);
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="pm-grid-lane-label pm-dept-label-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setDraft(label); setEditing(false); }
        }}
        // Stop the click propagating up to drag handlers etc on the
        // lane header.
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      className="pm-grid-lane-label pm-dept-label"
      role="button"
      tabIndex={0}
      title="Click to rename"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(true); } }}
    >
      {label}
    </span>
  );
};

export default EditableDepartmentLabel;
